import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import {
  getCategories,
  type CategoryItem,
  type CategorySummary,
  type CategoryPagination,
  type FetchCategoriesParams,
} from '../api/categories'

interface CategoriesState {
  summary: CategorySummary | null
  categories: CategoryItem[]
  pagination: CategoryPagination | null
  loading: boolean
  error: string | null
  activeTab: 'Active' | 'Inactive' | 'All'
  searchQuery: string
  currentPage: number
  limit: number
}

const initialState: CategoriesState = {
  summary: null,
  categories: [],
  pagination: null,
  loading: false,
  error: null,
  activeTab: 'All',
  searchQuery: '',
  currentPage: 1,
  limit: 25,
}

// Async thunk to fetch categories from GET /api/v1/erp/products/categories
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (params: FetchCategoriesParams | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { categories: CategoriesState }
      const currentParams: FetchCategoriesParams = {
        page: params?.page ?? state.categories.currentPage,
        limit: params?.limit ?? state.categories.limit,
        search: params?.search ?? state.categories.searchQuery,
        status: params?.status ?? state.categories.activeTab,
      }
      const response = await getCategories(currentParams)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch categories')
    }
  }
)

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<'Active' | 'Inactive' | 'All'>) {
      state.activeTab = action.payload
      state.currentPage = 1
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload
      state.currentPage = 1
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload
      state.currentPage = 1
    },
    clearCategoriesState(state) {
      state.summary = null
      state.categories = []
      state.pagination = null
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false
        const payload: any = action.payload
        if (Array.isArray(payload)) {
          state.categories = payload
          state.summary = null
          state.pagination = null
        } else if (payload && Array.isArray(payload.data)) {
          state.categories = payload.data
          state.summary = payload.summary || null
          state.pagination = payload.pagination || null
        } else if (payload && payload.data && Array.isArray(payload.data.categories)) {
          state.categories = payload.data.categories
          state.summary = payload.data.summary || payload.summary || null
          state.pagination = payload.data.pagination || payload.pagination || null
        } else if (payload && Array.isArray(payload.categories)) {
          state.categories = payload.categories
          state.summary = payload.summary || null
          state.pagination = payload.pagination || null
        } else {
          state.categories = []
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setActiveTab,
  setSearchQuery,
  setCurrentPage,
  setLimit,
  clearCategoriesState,
} = categoriesSlice.actions

export default categoriesSlice.reducer
